import React, { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import Textarea from "@mui/joy/Textarea";
import ReactMarkdown from "react-markdown";
import { Flag, ThumbsUp, ThumbsDown } from "lucide-react";
import { isArabic } from "../helpers/helpers";
import { SystemLanguage } from "../../common/system-language";
import { ReportModal } from "../report-modal/ReportModal";
import { reportMessage, ReportType } from "../../api/reportService";

interface Avatar {
    name: string;
}

interface ChatBoxProps {
    messages: any[];
    language: string;
    backgroundColor: number;
    onMessageSend: (message: string) => void;
    avatar?: Avatar;
    floatingMode?: boolean;
    isLoading?: boolean;
    systemLanguage?: SystemLanguage;
    readOnly?: boolean;
    textingEnabled?: boolean;
    interactionDisabled?: boolean;
    contentTopInset?: number;
    contentBottomInset?: number;
}

export const ChatBox: React.FC<ChatBoxProps> = React.memo(
    ({ messages, backgroundColor, onMessageSend, avatar, language, floatingMode, isLoading, systemLanguage = "en", readOnly = false, textingEnabled = true, interactionDisabled = false, contentTopInset = 0, contentBottomInset = 0 }) => {
        const [selectedResponses, setSelectedResponses] = useState<Record<number, string>>({});
        const [textArea, setTextArea] = useState("");
        const [reportModalOpen, setReportModalOpen] = useState(false);
        const [reportConversationId, setReportConversationId] = useState<string>("");
        const [reportedIds, setReportedIds] = useState<Set<string>>(() => {
            try {
                const stored = localStorage.getItem("reportedInteractions");
                return stored ? new Set(JSON.parse(stored)) : new Set();
            } catch { return new Set(); }
        });
        const containerRef = useRef<HTMLDivElement | null>(null);
        const isArabicUi = systemLanguage === "ar";
        const shouldRenderFullHistory = readOnly || !floatingMode;
        const readOnlyMessage = isArabicUi
            ? "هذه المحادثة للقراءة فقط"
            : "This conversation is read only";

        const botName = avatar?.name || "Bot";

        const rateResponse = async (e: React.MouseEvent, index: number, type: "up" | "down", conversationId: string) => {
            e.preventDefault();
            setSelectedResponses((prev) => ({
                ...prev,
                [index]: type,
            }));
            const { REACT_APP_AVATAR_RATE_RESPONSE } = process.env as any;
            await Axios.put(REACT_APP_AVATAR_RATE_RESPONSE.replace("{conversationId}", conversationId), {
                rate: type === "up" ? 1 : 0,
            });
        };

        const handleReportClick = (conversationId: string) => {
            setReportConversationId(conversationId);
            setReportModalOpen(true);
        };

        const handleReportSubmit = async (conversationId: string, reportType: ReportType, comment?: string) => {
            await reportMessage(conversationId, reportType, comment);
            const newReported = new Set(reportedIds);
            newReported.add(conversationId);
            setReportedIds(newReported);
            localStorage.setItem("reportedInteractions", JSON.stringify(Array.from(newReported)));
        };

        useEffect(() => {
            if (!containerRef.current) return;

            if (floatingMode && !readOnly) {
                setTimeout(() => {
                    if (containerRef.current) {
                        containerRef.current.scrollTop = 0;
                    }
                }, 0);
                return;
            }

            containerRef.current.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: "smooth",
            });
        }, [messages, floatingMode, readOnly]);

        const drawThumbs = (index: number, conversationId: string) => {
            return (
                <div>
                    <button
                        type="button"
                        onClick={(e) => rateResponse(e, index, "up", conversationId)}
                        className={selectedResponses[index] === "up" ? "thumb-selected" : ""}
                        style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
                    >
                        <ThumbsUp size={16} color="white" fill={selectedResponses[index] === "up" ? "white" : "none"} />
                    </button>
                    <button
                        type="button"
                        onClick={(e) => rateResponse(e, index, "down", conversationId)}
                        className={selectedResponses[index] === "down" ? "thumb-selected" : ""}
                        style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
                    >
                        <ThumbsDown size={16} color="white" fill={selectedResponses[index] === "down" ? "white" : "none"} />
                    </button>
                </div>
            );
        };

        const renderChat = () => {
            const fontColor = backgroundColor === 1 ? "color-black" : "color-white";
            const rateColor = backgroundColor === 1 ? "color-white" : "color-blue";
            const chatPointer = backgroundColor === 1 ? "white-pointer" : "blue-pointer";

            let visibleMessages: any[] = [];
            if (shouldRenderFullHistory) {
                visibleMessages = Array.isArray(messages) ? messages : [];
            } else {
                const list = Array.isArray(messages) ? messages : [];
                let lastUserIndex = -1;
                for (let i = list.length - 1; i >= 0; i--) {
                    const senderRaw = list[i] && list[i].sender;
                    const sender = senderRaw != null ? String(senderRaw).toLowerCase() : "";
                    if (sender === "client" || sender === "user") {
                        lastUserIndex = i;
                        break;
                    }
                }
                if (lastUserIndex !== -1) {
                    visibleMessages.push(list[lastUserIndex]);
                    const next = list[lastUserIndex + 1];
                    const nextSenderRaw = next && next.sender;
                    const nextSender = nextSenderRaw != null ? String(nextSenderRaw).toLowerCase() : "";
                    if (next && (nextSender === "ai" || nextSender === "assistant")) {
                        visibleMessages.push(next);
                    }
                } else {
                    visibleMessages = list.slice(-2);
                }
            }

            const messageElements = (Array.isArray(visibleMessages) ? visibleMessages.filter(Boolean) : []).map((message: any, index: number) => {
                const msg = message || { text: "", sender: "user", conversationId: "" };
                const isArabicText = isArabic(msg.text);
                return (
                    <div
                        key={`${msg.conversationId || index}`}
                        className={`${msg.sender} ${chatPointer}`}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            maxWidth: "85%",
                            wordWrap: "break-word",
                            overflowWrap: "break-word"
                        }}
                    >
                        <div
                            style={{
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                direction: isArabicText ? "rtl" : "ltr",
                                textAlign: isArabicText ? "right" : "left",
                                ...(msg.sender !== "ai" && { whiteSpace: "pre-wrap" })
                            }}
                            className={`${fontColor} ${msg.sender}-background ${msg.sender === "ai" ? "markdown-content" : ""}`}
                        >
                             {msg.sender === "ai" ? (
                                 <ReactMarkdown>{msg.text}</ReactMarkdown>
                             ) : (
                                 <p className="no-margin">{msg.text}</p>
                             )}
                         </div>
                         {msg.sender === "ai" && !readOnly ? (
                             <div className={`rate-response ${rateColor}`} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                 {drawThumbs(index, msg.conversationId || "")}
                                 <button
                                     onClick={() => handleReportClick(msg.conversationId || "")}
                                     disabled={reportedIds.has(String(msg.conversationId))}
                                     style={{
                                         background: "none",
                                         border: "none",
                                         cursor: reportedIds.has(String(msg.conversationId)) ? "not-allowed" : "pointer",
                                         opacity: reportedIds.has(String(msg.conversationId)) ? 0.4 : 1,
                                         padding: "2px",
                                         display: "flex",
                                         marginTop: 5,
                                         alignItems: "center",
                                         transition: "opacity 0.2s",
                                     }}
                                     onMouseEnter={(e) => { if (!reportedIds.has(String(msg.conversationId))) (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                                     onMouseLeave={(e) => { if (!reportedIds.has(String(msg.conversationId))) (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                                     title={reportedIds.has(String(msg.conversationId)) ? "Reported" : "Report"}
                                 >
                                     <Flag size={16} color="white" />
                                 </button>
                             </div>
                         ) : null}
                     </div>
                 )
             });

            return (
                <>
                    {messageElements}
                    {isLoading && (
                        <div className="ai" style={{ display: "flex", flexDirection: "column", maxWidth: "85%" }}>
                            <span className="typing-label">
                                {isArabicUi ? `${botName} يكتب...` : `${botName} is typing...`}
                            </span>
                            <div className="ai-background typing-indicator-bubble">
                                <div className="dot-typing"></div>
                            </div>
                        </div>
                    )}
                </>
            );
        };

        const sendMessage = useCallback(() => {
            if (!interactionDisabled && textArea.trim()) {
                onMessageSend(textArea.trim());
                setTextArea("");
            }
        }, [textArea, onMessageSend, interactionDisabled]);

        return (
            <div className="chat-box">
                <div
                    className="container-inner"
                    ref={containerRef}
                    style={{
                        paddingTop: contentTopInset,
                        paddingBottom: contentBottomInset,
                        scrollPaddingTop: contentTopInset,
                        scrollPaddingBottom: contentBottomInset,
                    }}
                >
                    {renderChat()}
                </div>
                <ReportModal
                    open={reportModalOpen}
                    conversationId={reportConversationId}
                    onClose={() => setReportModalOpen(false)}
                    onSubmit={handleReportSubmit}
                    systemLanguage={systemLanguage}
                />
                {readOnly ? (
                    <div
                        className="text-area"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "rgba(255, 255, 255, 0.75)",
                            fontSize: 14,
                            fontWeight: 500,
                            textAlign: "center",
                        }}
                    >
                        {readOnlyMessage}
                    </div>
                ) : !textingEnabled ? null : (
                    <div className="text-area">
                        <div className="text-input-wrapper">
                            <Textarea
                                value={textArea}
                                onChange={(e) => setTextArea(e.target.value)}
                                disabled={interactionDisabled}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage();
                                    }
                                }}
                                placeholder={isArabicUi ? "اكتب رسالة..." : "Type a message..."}
                                maxRows={3}
                                sx={{
                                    flex: 1,
                                    "--Textarea-focusedHighlight": "rgba(51, 51, 255, 0.3)",
                                    borderRadius: "20px",
                                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                                    color: "white",
                                    border: "1px solid rgba(255, 255, 255, 0.25)",
                                    "& textarea::placeholder": { color: "rgba(255, 255, 255, 0.5)" },
                                    "& textarea": { color: "white" },
                                }}
                            />
                            <button
                                onClick={sendMessage}
                                className="send-btn"
                                disabled={interactionDisabled || !textArea.trim()}
                            >
                                <i className="fa fa-paper-plane"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
);
